/**
 * Excel Insights Component
 * ========================
 * Displays Excel-specific analysis, statistics, and insights
 *
 * Features:
 * - Summary statistics
 * - Table view with sorting/filtering
 * - Chart gallery
 * - AI-generated insights
 * - Data quality indicators
 */

import React, { useState, useMemo } from 'react';

import {
  Table2,
  BarChart3,
  Lightbulb,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  FileSpreadsheet,
  Columns,
  Rows,
  Layers,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';

import ChartGallery from '@/components/smart-notebook/ExcelInsights/ChartGallery';
import IntelligenceNotes from '@/components/smart-notebook/ExcelInsights/IntelligenceNotes';
import SummaryBox from '@/components/smart-notebook/ExcelInsights/SummaryBox';
import TableView from '@/components/smart-notebook/ExcelInsights/TableView';
import '@/components/smart-notebook/ExcelInsights/ExcelInsights.css';

export default function ExcelInsights({
  metadata,
  table,
  charts,
  insights,
  verification,
  schema,
  analysis,
  intelligence,
  onQueryRequest,
}) {
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    table: false,
    charts: false,
    insights: true,
  });

  const toggleSection = section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Calculate overall data quality
  const dataQuality = useMemo(() => {
    if (analysis?.dataQuality) return analysis.dataQuality;
    if (metadata?.dataQuality) return metadata.dataQuality;
    return null;
  }, [analysis, metadata]);

  // Get tab items
  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileSpreadsheet },
    { id: 'table', label: 'Data', icon: Table2, badge: metadata?.totalRows || table?.length },
    { id: 'charts', label: 'Charts', icon: BarChart3, badge: charts?.length },
    {
      id: 'insights',
      label: 'Insights',
      icon: Lightbulb,
      badge: insights?.length || intelligence?.insights?.length,
    },
  ];

  return (
    <div className="excel-insights">
      {/* Header */}
      <div className="excel-insights-header">
        <div className="excel-insights-title">
          <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
          <h3>Excel Analysis</h3>
        </div>

        {/* Verification Badge */}
        {verification && (
          <div className={`verification-badge ${verification.verified ? 'verified' : 'warning'}`}>
            {verification.verified ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Verified</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Unverified</span>
              </>
            )}
            <span className="confidence">({(verification.confidence * 100).toFixed(0)}%)</span>
          </div>
        )}

        {/* Data Quality Badge */}
        {dataQuality && (
          <div className={`quality-badge quality-${dataQuality.grade?.toLowerCase()}`}>
            <span className="grade">{dataQuality.grade}</span>
            <span className="score">{dataQuality.score}/100</span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="excel-insights-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
            {tab.badge && <span className="tab-badge">{tab.badge > 999 ? '999+' : tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="excel-insights-content">
        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="tab-content">
            <SummaryBox
              metadata={metadata}
              schema={schema}
              analysis={analysis}
              intelligence={intelligence}
            />
          </div>
        )}

        {/* Table Tab */}
        {activeTab === 'table' && (
          <div className="tab-content">
            <TableView
              data={table || []}
              schema={schema}
              maxRows={100}
              onQueryRequest={onQueryRequest}
            />
          </div>
        )}

        {/* Charts Tab */}
        {activeTab === 'charts' && (
          <div className="tab-content">
            <ChartGallery charts={charts || []} intelligence={intelligence} />
          </div>
        )}

        {/* Insights Tab */}
        {activeTab === 'insights' && (
          <div className="tab-content">
            <IntelligenceNotes
              insights={insights || intelligence?.insights || []}
              anomalies={intelligence?.anomalies || []}
              trends={intelligence?.trends || []}
              correlations={intelligence?.correlations || []}
              kpis={intelligence?.kpis || []}
              onQueryRequest={onQueryRequest}
            />
          </div>
        )}
      </div>

      {/* Footer with warnings */}
      {verification?.warnings && verification.warnings.length > 0 && (
        <div className="excel-insights-warnings">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span>{verification.warnings[0]}</span>
          {verification.warnings.length > 1 && (
            <span className="more-warnings">+{verification.warnings.length - 1} more</span>
          )}
        </div>
      )}
    </div>
  );
}
